import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { setInlineCommentMode, setInlineCommentReference } from 'actions';
import moment from 'moment';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getSingleEnvelopeComments } from 'selectors/envelopes';
import { useSelector } from 'store';
import { RenderedAction } from 'types';
import LoadHtmlPlugin from 'utils/lexical/LoadHtmlPlugin';

type ComponentProps = {
  action: RenderedAction;
};

const EnvelopeSessionCommentItem: React.FC<ComponentProps> = (props) => {
  const dispatch = useDispatch();

  const { action } = props;
  const { envelopeId } = useParams<{ envelopeId: string }>();

  const comments = useSelector((state) => getSingleEnvelopeComments(state, envelopeId));

  const [textContent, setTextContent] = useState('');
  const [showMore, setShowMore] = useState(false);
  const [clampedComment, setClampedComment] = useState(false);

  const comment = comments.filter((c) => !c.deleted_at).find((c) => c.uuid === action.uuid);

  if (!comment) {
    return (
      <div className="ml-6 flex flex-col gap-1">
        <li>
          <span className="line-through text-litlingo-gray-5">
            Commented at {moment(action.created_at).format('hh:mm a')}
          </span>
          <span className="italic text-litlingo-gray-3"> (deleted)</span>
        </li>
      </div>
    );
  }

  const isInlineComment =
    // @ts-ignore
    comment.payload?.lineIndex !== undefined &&
    // @ts-ignore
    comment.payload.nodes?.[0] !== undefined &&
    // @ts-ignore
    comment.payload.communicationId;

  const commentRef = (node: HTMLDivElement): void => {
    if (node) {
      const newC = node.scrollHeight > node.clientHeight + 2;
      setClampedComment(newC);
    }
  };

  const commentConfig: InitialConfigType = {
    namespace: 'comment-action',
    editable: false,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any): void => {
      throw error;
    },
  };

  const handleClickInlineComment = (): void => {
    dispatch(setInlineCommentMode(true));
    dispatch(
      setInlineCommentReference({
        // @ts-ignore
        communicationId: comment?.payload.communicationId,
        // @ts-ignore
        lineNumber: comment?.payload.lineIndex,
        // @ts-ignore
        nodes: comment?.payload.nodes,
        text: '',
      })
    );
  };

  return (
    <div className="ml-6 flex flex-col gap-1">
      {isInlineComment ? (
        <li>Added an inline comment at {moment(action.created_at).format('hh:mm a')}</li>
      ) : (
        <li>Commented at {moment(action.created_at).format('hh:mm a')}</li>
      )}
      <button
        type="button"
        className={`w-full py-1 px-2 flex items-center text-body bg-litlingo-gray-1 rounded focus:outline-none ${
          isInlineComment ? '' : 'cursor-default'
        }`}
        onClick={handleClickInlineComment}
      >
        <div
          ref={commentRef as React.LegacyRef<HTMLDivElement>}
          className={`w-full flex text-left text-small ${showMore ? '' : 'line-clamp-4'}`}
        >
          {textContent}
        </div>

        <LexicalComposer initialConfig={commentConfig}>
          <LoadHtmlPlugin
            edited={false}
            content={action.description.split(':', 2)[1] || ''}
            setTextContent={setTextContent}
          />
        </LexicalComposer>
      </button>
      <div className="flex flex-row items-start justify-between text-small">
        <div>
          {comment && !moment(comment.created_at).isSame(moment(comment.updated_at)) && (
            <p className="mb-2 text-litlingo-gray-4">
              Edited {moment(comment.updated_at).format('yyyy MMM DD hh:mma')}
            </p>
          )}
        </div>
        {(showMore || clampedComment) && (
          <button
            type="button"
            className="text-litlingo-primary underline focus:outline-none"
            onClick={(): void => {
              setShowMore(!showMore);
            }}
          >
            {showMore ? 'Show Less' : 'Show More'}
          </button>
        )}
      </div>
    </div>
  );
};

export default EnvelopeSessionCommentItem;
